const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ATTACHMENT_TICKER_RE = /^([a-z0-9\^=]+)(?:_usd)?_lppls_fit_/i;

function startOfDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function addDays(date, days) {
  return new Date(startOfDay(date).getTime() + days * MS_PER_DAY);
}

function diffDays(a, b) {
  return Math.abs(startOfDay(a).getTime() - startOfDay(b).getTime()) / MS_PER_DAY;
}

function toDateFromTc(value) {
  if (!value) return null;
  const match = String(value).match(/\d{4}-\d{2}-\d{2}/);
  if (!match) return null;
  return new Date(match[0]);
}

function isMLSLTrue(value) {
  return value === true || value === 1 || value === "1" || value === "true" || value === "TRUE";
}

export function toFloat(value) {
  const parsed = parseFloat(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseConf01(value) {
  const parsed = toFloat(value);
  if (parsed <= 0) return 0;
  if (parsed > 1) return Math.min(1, parsed / 100);
  return Math.min(1, parsed);
}

function parseTimeframeDays(value) {
  const match = String(value || "").match(/(\d+)/);
  if (!match) return 9999;
  const parsed = parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : 9999;
}

function normalizeDir(direction) {
  const source = String(direction || "").toLowerCase();
  if (source.includes("down")) return "Downward";
  if (source.includes("up")) return "Upward";
  return "Mixed";
}

function dominantDirection(rows) {
  let up = 0;
  let down = 0;
  rows.forEach((row) => {
    const direction = normalizeDir(row.direction);
    if (direction === "Upward") up += 1;
    if (direction === "Downward") down += 1;
  });
  if (up && down) return "Mixed";
  if (down) return "Downward";
  return "Upward";
}

function computeWeight(row) {
  const confidence = parseConf01(row.confidence);
  const rSquared = toFloat(row.r_squared);
  const rmse = toFloat(row.rmse);
  const timeframeDays = parseTimeframeDays(row.horizon);

  const mlslMult = isMLSLTrue(row.mlsl) ? 1.5 : 1;
  const r2Score = Math.max(0, Math.min(1, (rSquared - 0.8) / 0.15));
  const rmseScore = 1 / (1 + 20 * rmse);
  const tfMult = timeframeDays <= 60 ? 1.2 : timeframeDays <= 120 ? 1.0 : 0.85;

  return confidence * mlslMult * r2Score * rmseScore * tfMult;
}

function confidencePct(consensusStrength, clusterAvgConf) {
  return Math.round(100 * (0.6 * consensusStrength + 0.4 * clusterAvgConf));
}

function badgeType({ dir, daysToCenter, confPct, minTF }) {
  const daysAbs = Math.abs(daysToCenter);
  if (dir === "Downward" && daysAbs <= 14 && confPct >= 60) return "Crash";
  if ((dir === "Upward" || dir === "Mixed") && minTF <= 60 && confPct >= 45) return "Pullback";
  return "Volatility";
}

export function computeMostProbableTcWindow(rows, windowDays, includePastDays = 0) {
  const winN = Math.max(1, parseInt(windowDays, 10) || 3);
  const today = startOfDay(new Date());
  const minDate = includePastDays > 0 ? addDays(today, -includePastDays) : today;

  const allRows = (rows || [])
    .map((row) => ({
      ...row,
      tc: toDateFromTc(row.tc_date),
      weight: computeWeight(row),
      conf01: parseConf01(row.confidence),
      timeframeDays: parseTimeframeDays(row.horizon),
    }))
    .filter((row) => row.tc && row.weight > 0);

  const future = allRows.filter((row) => row.tc >= minDate);
  const candidates = future.length ? future : allRows;
  if (!candidates.length) return null;

  const centerMap = new Map();
  candidates.forEach((row) => centerMap.set(startOfDay(row.tc).toISOString(), startOfDay(row.tc)));
  const centers = Array.from(centerMap.values());

  let bestCenter = centers[0];
  let bestWeight = -1;

  centers.forEach((center) => {
    const weight = candidates.reduce((sum, row) => (diffDays(row.tc, center) <= winN ? sum + row.weight : sum), 0);
    if (weight > bestWeight) {
      bestWeight = weight;
      bestCenter = center;
    }
  });

  const cluster = candidates.filter((row) => diffDays(row.tc, bestCenter) <= winN);
  if (!cluster.length) {
    const nearest = candidates.slice().sort((a, b) => diffDays(a.tc, bestCenter) - diffDays(b.tc, bestCenter))[0];
    if (nearest) cluster.push(nearest);
  }

  const totalWeight = candidates.reduce((sum, row) => sum + row.weight, 0);
  const clusterWeight = cluster.reduce((sum, row) => sum + row.weight, 0);
  const averageConfidence = cluster.reduce((sum, row) => sum + (row.conf01 || 0), 0) / cluster.length;
  const consensusStrength = clusterWeight / (totalWeight || 1);
  const confPct = confidencePct(consensusStrength, averageConfidence);
  const centerMs = cluster.reduce((sum, row) => sum + row.tc.getTime() * row.weight, 0) / (clusterWeight || 1);
  const center = startOfDay(new Date(centerMs));
  const daysToCenter = (center.getTime() - today.getTime()) / MS_PER_DAY;
  const minTF = Math.min(...cluster.map((row) => row.timeframeDays || 9999));
  const dir = dominantDirection(cluster);

  return {
    windowStart: addDays(center, -winN),
    windowEnd: addDays(center, winN),
    center,
    daysToCenter,
    badge: badgeType({ dir, daysToCenter, confPct, minTF }),
    confPct,
    consensusPct: Math.round(consensusStrength * 100),
    avgConfPct: Math.round(averageConfidence * 100),
  };
}

export function sanitizeSyncMessage(message, fallback) {
  if (!message) return fallback;
  const normalized = String(message);
  if (normalized.toLowerCase().includes("successfully synced")) {
    return fallback;
  }
  return normalized;
}

export function buildAttachmentUrl(apiBase, reportId, filename) {
  return `${apiBase}/attachments/${reportId}/${encodeURIComponent(filename)}`;
}

export function inferAttachmentTicker(filename) {
  const match = String(filename || "").toLowerCase().match(ATTACHMENT_TICKER_RE);
  return match ? match[1] : "";
}

export function getSymbolAttachmentKeys(symbol) {
  const raw = String(symbol || "").toLowerCase().trim();
  const normalized = raw.replace(/^\^/, "");
  const keys = new Set();

  [raw, normalized].filter(Boolean).forEach((value) => keys.add(value));
  const dashBase = normalized.split("-")[0];
  if (dashBase) keys.add(dashBase);

  const underscore = normalized.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (underscore) keys.add(underscore);

  if (normalized.endsWith("-usd")) keys.add(normalized.replace(/-usd$/, ""));
  if (normalized.endsWith("=x")) keys.add(normalized.replace(/=x$/, ""));
  if (normalized.endsWith("=f")) keys.add(normalized.replace(/=f$/, ""));

  const aliases = {
    "gc=f": ["gold", "gc"],
    "si=f": ["silver", "si"],
    "cl=f": ["oil", "cl"],
    "nokeur=x": ["nokeur"],
    "nokusd=x": ["nokusd"],
    "^gspc": ["gspc"],
    "^ndx": ["ndx"],
  };
  (aliases[raw] || []).forEach((value) => keys.add(value));

  return Array.from(keys).filter(Boolean);
}

export function matchesAttachmentForSymbol(filename, symbol) {
  const keys = getSymbolAttachmentKeys(symbol);
  const inferredTicker = inferAttachmentTicker(filename);
  if (inferredTicker && keys.includes(inferredTicker)) {
    return true;
  }
  const lowerFilename = String(filename || "").toLowerCase();
  return keys.some((key) => lowerFilename.includes(key));
}

export function formatLocalBottomLabel(label) {
  const source = String(label || "").replace(/LOCAL BOTTOM/gi, "__LOCAL_BOTTOM__");
  return source
    .replace(/BOTTOM IN/gi, "LOCAL BOTTOM IN")
    .replace(/BOTTOM WATCH/gi, "LOCAL BOTTOM WATCH")
    .replace(/BOTTOM NOT IN/gi, "LOCAL BOTTOM NOT IN")
    .replace(/__LOCAL_BOTTOM__/g, "LOCAL BOTTOM");
}

export function formatLocalBottomText(text) {
  const source = String(text || "").replace(/LOCAL BOTTOM/gi, "__LOCAL_BOTTOM__");
  return source
    .replace(/Bottom In:/g, "Local Bottom In:")
    .replace(/BOTTOM IN/g, "LOCAL BOTTOM IN")
    .replace(/Bottom In/g, "Local Bottom In")
    .replace(/Bottom Watch/g, "Local Bottom Watch")
    .replace(/BOTTOM WATCH/g, "LOCAL BOTTOM WATCH")
    .replace(/Bottom Not In/g, "Local Bottom Not In")
    .replace(/BOTTOM NOT IN/g, "LOCAL BOTTOM NOT IN")
    .replace(/__LOCAL_BOTTOM__/g, "LOCAL BOTTOM");
}